import React, { useState, useRef } from "react";
import { Box, Typography, Button, Grid, FormControl, FormLabel, TextField, Select, MenuItem, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import styled from "styled-components";
import { postData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
  },
}));

const labelStyle = { color: "#8F95B2", fontSize: "13px" };

const EXAM_OPTIONS = ["Mid Term", "Final Term", "Unit Test", "Mock Test"];
const PAPER_TYPE_OPTIONS = ["Written", "Oral", "Practice", "Model"];
const STATUS_OPTIONS = ["Finished", "Upcoming", "Draft"];

export default function QuestionPaperForm({ handleClose, initialData }) {
  const isEdit = Boolean(initialData?.id);
  const [form, setForm] = useState({
    grade: initialData?.grade || "",
    section: initialData?.section || "",
    class: initialData?.class || "",
    academicYear: initialData?.academicYear || "2025-2026",
    term: initialData?.term || "",
    subject: initialData?.subject || "",
    exam: initialData?.exam || initialData?.examType || "",
    questionPaperType: initialData?.questionPaperType || "",
    status: initialData?.status || "",
  });
  const [file, setFile] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const fileRef = useRef(null);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(form).forEach((k) => formData.append(k, form[k]));
      if (file) formData.append("file", file);
      if (isEdit) {
        await putData(`teacher/question-paper/${initialData.id}`, formData);
        setToast({ open: true, severity: "success", message: "Question paper updated successfully" });
      } else {
        await postData("teacher/question-paper/create", formData);
        setToast({ open: true, severity: "success", message: "Question paper uploaded successfully" });
      }
      setTimeout(handleClose, 1200);
    } catch (_) {
      setToast({ open: true, severity: "error", message: isEdit ? "Update failed" : "Upload failed" });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: "24px" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>{isEdit ? "Edit Question Paper" : "Upload Question Paper"}</Typography>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px" }}>Fill in the details and attach the paper.</Typography>
        </Box>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Grade</FormLabel>
            <StyledTextField size="small" name="grade" value={form.grade} onChange={handleChange} placeholder="e.g. Grade 3" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Section</FormLabel>
            <StyledTextField size="small" name="section" value={form.section} onChange={handleChange} placeholder="e.g. A" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>CLASS</FormLabel>
            <StyledTextField size="small" name="class" value={form.class} onChange={handleChange} placeholder="e.g. Nilai 01" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Academic Year</FormLabel>
            <Select size="small" name="academicYear" value={form.academicYear} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              <MenuItem value="2025-2026">2025-2026</MenuItem>
              <MenuItem value="2026-2027">2026-2027</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Term</FormLabel>
            <StyledTextField size="small" name="term" value={form.term} onChange={handleChange} placeholder="e.g. Term 1" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Subject</FormLabel>
            <Select size="small" name="subject" value={form.subject} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              <MenuItem value="Tamil Reading">Tamil Reading</MenuItem>
              <MenuItem value="Tamil Writing">Tamil Writing</MenuItem>
              <MenuItem value="Tamil Grammar">Tamil Grammar</MenuItem>
              <MenuItem value="Tamil Speaking">Tamil Speaking</MenuItem>
              <MenuItem value="Homework">Homework</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Exam</FormLabel>
            <Select size="small" name="exam" value={form.exam} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              {EXAM_OPTIONS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Question Paper Type</FormLabel>
            <Select size="small" name="questionPaperType" value={form.questionPaperType} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              {PAPER_TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Status</FormLabel>
            <Select size="small" name="status" value={form.status} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            onClick={() => fileRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
            onDragOver={(e) => e.preventDefault()}
            sx={{ border: "2px dashed #D5D7DA", borderRadius: "10px", p: "24px", textAlign: "center", cursor: "pointer", backgroundColor: "#fafafa" }}
          >
            <input type="file" ref={fileRef} style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0])} />
            <CloudUploadOutlinedIcon sx={{ fontSize: "40px", color: "#8F95B2" }} />
            <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 1 }}>
              {file ? file.name : (isEdit && initialData?.fileName) ? `Current: ${initialData.fileName} (click to replace)` : "Click to upload or drag and drop"}
            </Typography>
            <Typography sx={{ color: "#bbb", fontSize: "11px" }}>SVG, PNG, JPG or GIF (max. 800×400px)</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ color: "#4caf50", borderColor: "#4caf50", borderRadius: "10px", textTransform: "none", px: 4 }}>Cancel</Button>
        <Button type="submit" sx={{ backgroundColor: "#1a237e", color: "#fff", borderRadius: "10px", textTransform: "none", px: 4, "&:hover": { backgroundColor: "#283593" } }}>
          {isEdit ? "Update" : "Submitted"}
        </Button>
      </Box>
    </Box>
  );
}
